import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

// Two-click rectangle draw mode for MapboxDraw
const DrawRectangleMode = {
    onSetup() {
        this.map.dragPan.disable();
        const rectangle = this.newFeature({
            type: 'Feature',
            properties: {},
            geometry: { type: 'Polygon', coordinates: [[]] },
        });
        return { rectangle, startPoint: null };
    },

    onClick(state, e) {
        const pt = [e.lngLat.lng, e.lngLat.lat];
        if (!state.startPoint) {
            state.startPoint = pt;
            return;
        }
        // Second click — close the rectangle
        const [x1, y1] = state.startPoint;
        const [x2, y2] = pt;
        this.addFeature(state.rectangle);
        state.rectangle.updateCoordinate('0.0', x1, y1);
        state.rectangle.updateCoordinate('0.1', x2, y1);
        state.rectangle.updateCoordinate('0.2', x2, y2);
        state.rectangle.updateCoordinate('0.3', x1, y2);
        state.rectangle.updateCoordinate('0.4', x1, y1);
        this.changeMode('simple_select', { featureIds: [state.rectangle.id] });
    },

    onMouseMove(state, e) {
        if (!state.startPoint) return;
        const [x1, y1] = state.startPoint;
        const x2 = e.lngLat.lng;
        const y2 = e.lngLat.lat;
        state.rectangle.updateCoordinate('0.0', x1, y1);
        state.rectangle.updateCoordinate('0.1', x2, y1);
        state.rectangle.updateCoordinate('0.2', x2, y2);
        state.rectangle.updateCoordinate('0.3', x1, y2);
        state.rectangle.updateCoordinate('0.4', x1, y1);
    },

    toDisplayFeatures(state, geojson, display) {
        display(geojson);
    },

    onStop(state) {
        this.map.dragPan.enable();
        if (!state.startPoint) {
            this.deleteFeature([state.rectangle.id], { silent: true });
        }
    },

    onKeyUp(state, e) {
        if (e.keyCode === 27) this.changeMode('simple_select');
    },
};

function isRectangle(pts) {
    const lngs = pts.map(c => c[0]);
    const lats = pts.map(c => c[1]);
    const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats);

    const corners = [
        [minLng, minLat],
        [maxLng, minLat],
        [maxLng, maxLat],
        [minLng, maxLat],
    ];

    return pts.every(pt => {
        return corners.some(c => 
            Math.abs(pt[0] - c[0]) < 0.000001 && Math.abs(pt[1] - c[1]) < 0.000001
        );
    });
}

// Convert a drawn feature to an area string for HERE API / storage.
// Rectangles (4-point polygons) become bbox: strings; everything else becomes polygon: strings.
function featureToAreaString(feature) {
    const coords = feature.geometry.coordinates[0];
    const pts = coords.slice(0, -1); // drop closing point
    if (pts.length === 4 && isRectangle(pts)) {
        const lngs = pts.map(c => c[0]);
        const lats = pts.map(c => c[1]);
        const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
        const minLat = Math.min(...lats), maxLat = Math.max(...lats);
        return `bbox:${minLng.toFixed(6)},${minLat.toFixed(6)},${maxLng.toFixed(6)},${maxLat.toFixed(6)}`;
    }
    return 'polygon:' + pts.map(c => `${c[1].toFixed(6)},${c[0].toFixed(6)}`).join(';');
}

function areaStringToFeature(str) {
    if (str.startsWith('bbox:')) {
        const [minLng, minLat, maxLng, maxLat] = str.replace('bbox:', '').split(',').map(Number);
        return {
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'Polygon',
                coordinates: [[
                    [minLng, minLat], [maxLng, minLat], [maxLng, maxLat],
                    [minLng, maxLat], [minLng, minLat],
                ]],
            },
        };
    }
    if (str.startsWith('polygon:')) {
        const points = str.replace('polygon:', '').split(';').filter(Boolean);
        const coords = points.map(p => {
            const [lat, lon] = p.split(',').map(Number);
            return [lon, lat];
        });
        coords.push(coords[0]); // close
        return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } };
    }
    return null;
}

function areaLabel(str) {
    if (str.startsWith('bbox:')) return str;
    if (str.startsWith('polygon:')) {
        const n = str.replace('polygon:', '').split(',').length / 2;
        return `Polygon (${n} pts)`;
    }
    return str;
}

class AvoidAreasEditor extends HTMLElement {
    constructor() {
        super();
        this._areas = []; // { id: string, area: string }
        this._map = null;
        this._draw = null;
    }

    connectedCallback() {
        const raw = (this.getAttribute('value') || '').trim();
        if (raw) {
            this._areas = raw.split('\n').map(s => s.trim()).filter(Boolean)
                .map(area => ({ id: null, area }));
        }
        this._render();
        this._initMap();
    }

    _render() {
        this.innerHTML = `
            <div>
                <div id="avoid-areas-map" style="width:100%;height:500px;border-radius:6px;overflow:hidden;"></div>
                <ul id="avoid-areas-list" style="list-style:none;padding:0;margin:8px 0 0;"></ul>
                <input type="hidden" name="avoid_areas" value="">
            </div>
        `;
        this._syncInput();
        this._renderList();
    }

    _initMap() {
        const mapboxgl = window.mapboxgl;
        if (!mapboxgl) {
            console.error('avoid-areas-editor: window.mapboxgl not available');
            return;
        }

        mapboxgl.accessToken = window.bevr.mapboxAccessToken;

        const container = this.querySelector('#avoid-areas-map');
        this._map = new mapboxgl.Map({
            container,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [18, 59],
            zoom: 5,
        });

        this._draw = new MapboxDraw({
            modes: { ...MapboxDraw.modes, draw_rectangle: DrawRectangleMode },
            displayControlsDefault: false,
            controls: { polygon: true, trash: true },
        });
        this._map.addControl(this._draw);

        this._map.on('load', () => {
            if (this._areas.length > 0) {
                const features = this._areas.map(a => areaStringToFeature(a.area)).filter(Boolean);
                const fc = { type: 'FeatureCollection', features };
                const ids = this._draw.add(fc);
                this._areas = this._areas.map((a, i) => ({ ...a, id: ids[i] }));
                this._renderList();

                const bounds = this._combinedBounds();
                if (bounds) this._map.fitBounds(bounds, { padding: 50 });
            }
            this._syncInput();
        });

        this._map.on('draw.create', e => {
            for (const feature of e.features) {
                this._areas.push({ id: feature.id, area: featureToAreaString(feature) });
            }
            this._renderList();
            this._syncInput();
        });

        this._map.on('draw.delete', e => {
            const deletedIds = new Set(e.features.map(f => f.id));
            this._areas = this._areas.filter(a => !deletedIds.has(a.id));
            this._renderList();
            this._syncInput();
        });
    }

    _combinedBounds() {
        if (this._areas.length === 0) return null;
        let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
        for (const { area } of this._areas) {
            const f = areaStringToFeature(area);
            if (!f) continue;
            for (const [lng, lat] of f.geometry.coordinates[0]) {
                if (lng < minLng) minLng = lng;
                if (lat < minLat) minLat = lat;
                if (lng > maxLng) maxLng = lng;
                if (lat > maxLat) maxLat = lat;
            }
        }
        return [[minLng, minLat], [maxLng, maxLat]];
    }

    _renderList() {
        const ul = this.querySelector('#avoid-areas-list');
        if (!ul) return;
        ul.innerHTML = '';
        this._areas.forEach(({ id, area }) => {
            const li = document.createElement('li');
            li.style.cssText = 'display:flex;align-items:center;gap:8px;margin-bottom:4px;font-size:0.85em;';
            li.innerHTML = `<span style="font-family:monospace;">${areaLabel(area)}</span>
                <button type="button" style="padding:2px 8px;border:1px solid #ccc;border-radius:3px;cursor:pointer;background:#fff;color:#c00;">Remove</button>`;
            li.querySelector('button').addEventListener('click', () => {
                if (this._draw) this._draw.delete([id]);
                this._areas = this._areas.filter(a => a.id !== id);
                this._renderList();
                this._syncInput();
            });
            ul.appendChild(li);
        });
    }

    _syncInput() {
        const input = this.querySelector('input[name="avoid_areas"]');
        if (input) input.value = this._areas.map(a => a.area).join('\n');
    }
}

customElements.define('avoid-areas-editor', AvoidAreasEditor);
